import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, Table, Button, Switch, Space, Tag,
  Typography, message, Spin, Empty,
} from 'antd';
import {
  GlobalOutlined, EyeOutlined, SettingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { marketingSiteAPI } from '../services/api';

const { Title, Text } = Typography;

export default function MarketingSiteListPage() {
  const { t } = useTranslation();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchSites = useCallback(async () => {
    setLoading(true);
    try {
      const res = await marketingSiteAPI.list();
      setSites(res.data);
    } catch (e) {
      message.error(t('common.loadFailed') + ': ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  const handleTogglePublish = async (record) => {
    try {
      await marketingSiteAPI.update(record.id, { is_published: !record.is_published });
      message.success(record.is_published ? t('sites.unpublishSuccess') : t('sites.publishSuccess'));
      fetchSites();
    } catch (e) {
      message.error(t('common.updateFailed') + ': ' + e.message);
    }
  };

  const getPublicUrl = (record) => {
    const path = record.public_url || `/site/${record.slug}`;
    return `${window.location.origin}${path}`;
  };

  const columns = [
    {
      title: t('sites.table.title'), dataIndex: 'title', ellipsis: true,
      render: (title) => <Text strong>{title}</Text>,
    },
    {
      title: t('sites.table.slug'), dataIndex: 'slug',
      render: (slug) => <Text code>{slug}</Text>,
    },
    {
      title: t('sites.table.publicUrl'), key: 'public_url',
      render: (_, record) => {
        const url = getPublicUrl(record);
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        );
      },
      ellipsis: true,
    },
    {
      title: t('sites.table.status'), dataIndex: 'is_published', width: 100,
      render: (v) => v ? <Tag color="green">{t('common.published')}</Tag> : <Tag>{t('common.draft')}</Tag>,
    },
    {
      title: t('sites.table.owner'), dataIndex: 'owner_username', width: 120,
      render: (v) => v || '-',
    },
    {
      title: t('sites.table.createdAt'), dataIndex: 'created_at', width: 170,
      render: (v) => v ? new Date(v).toLocaleString('zh-TW') : '-',
    },
    {
      title: t('sites.table.actions'), width: 260,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => navigate(`/marketing-sites/${record.id}`)}
          >
            {t('common.manage')}
          </Button>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => window.open(getPublicUrl(record), '_blank')}
          >
            {t('common.view')}
          </Button>
          <Switch
            size="small"
            checked={record.is_published}
            onChange={() => handleTogglePublish(record)}
            checkedChildren={t('common.published')}
            unCheckedChildren={t('common.draft')}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        style={{ marginBottom: 24 }}
        bodyStyle={{ padding: 0 }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '32px 32px 28px',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Space align="center" size={16}>
            <GlobalOutlined style={{ fontSize: 32, color: '#fff' }} />
            <div>
              <Title level={3} style={{ margin: 0, color: '#fff' }}>{t('sites.pageTitle')}</Title>
              <Text style={{ color: 'rgba(255,255,255,0.85)' }}>{t('sites.pageSubtitle')}</Text>
            </div>
          </Space>
        </div>
      </Card>

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : sites.length === 0 ? (
          <Empty
            description={t('sites.noSites')}
            style={{ padding: 60 }}
          >
            <Button
              type="primary"
              icon={<GlobalOutlined />}
              onClick={() => navigate('/scenarios')}
            >
              {t('sites.createFromScenarios')}
            </Button>
          </Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={sites}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20 }}
            size="middle"
          />
        )}
      </Card>
    </div>
  );
}
